import React, { useState, useEffect, useMemo } from 'react';
import { UserGroupIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import MarkdownRenderer from '../../MarkdownRenderer';
import { useTheme } from '../../../context/ThemeContext';
import Card from '../Card';
import Table from '../Table';
import Modal from '../Modal';
import Pagination from '../Pagination';
import SearchFilter from '../SearchFilter';

const UsersSection = ({ api, organizations = [], tariffs = [], onRefresh }) => {
  const { theme } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search & filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userReport, setUserReport] = useState(null);
  const [expandedReport, setExpandedReport] = useState(null);
  const [editForm, setEditForm] = useState({ username: '', email: '', role: '', is_active: false, tokens_balance: 0, organization_id: null });
  const [tariffForm, setTariffForm] = useState({ tariff_id: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const usersData = await api.fetchUsers();
      setUsers(usersData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => 
    users.filter(u => {
      const matchesSearch = (u.username || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = !roleFilter || u.role === roleFilter;
      return matchesSearch && matchesRole;
    }), [users, searchQuery, roleFilter]);

  const uniqueRoles = useMemo(() => 
    [...new Set(users.map(u => u.role).filter(Boolean))].sort(), [users]);

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage);

  const handleEdit = (user) => {
    setCurrentUser(user);
    setEditForm({
      username: user.username,
      email: user.email,
      role: user.role,
      is_active: user.is_active,
      tokens_balance: user.tokens_balance || 0,
      organization_id: user.organization_id || null,
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.updateUser(currentUser.id, editForm);
      toast.success('Пользователь обновлен');
      setShowEditModal(false);
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Удалить пользователя?')) return;
    try {
      await api.deleteUser(userId);
      toast.success('Пользователь удален');
      loadData();
      onRefresh?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleReport = async (userId) => {
    try {
      const report = await api.fetchUserReport(userId);
      setUserReport(report);
      setShowReportModal(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAssignTariff = (user) => {
    setCurrentUser(user);
    setTariffForm({ tariff_id: user.tariff_id || '' });
    setShowTariffModal(true);
  };

  const handleSaveTariff = async () => {
    try {
      await api.assignTariffToUser(currentUser.id, tariffForm.tariff_id || null);
      toast.success('Тариф назначен');
      setShowTariffModal(false);
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const roleLabels = { user: 'Пользователь', moderator: 'Модератор', admin: 'Админ', top_boss: 'Топ босс' };

  const userColumns = [
    { header: 'Имя', render: (row) => <span className="text-gray-900 dark:text-gray-100">{row.username}</span> },
    { header: 'Email', render: (row) => <span className="text-gray-700 dark:text-gray-300">{row.email}</span> },
    { header: 'Роль', className: 'hidden xs:table-cell', render: (row) => (
      <span className={`px-2 py-0.5 rounded text-xs ${row.role === 'admin' || row.role === 'top_boss' ? 'bg-red-100 text-red-800' : row.role === 'moderator' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
        {roleLabels[row.role] || row.role}
      </span>
    )},
    { header: 'Организация', className: 'hidden md:table-cell', render: (row) => <span className="text-gray-500 dark:text-gray-400 text-xs">{row.organization_name || '—'}</span> },
    { header: 'Действия', render: (row) => (
      <div className="flex gap-1 flex-wrap">
        <button onClick={() => handleEdit(row)} className="bg-blue-600 text-white rounded px-2 py-1 hover:bg-blue-700 text-xs"><PencilIcon className="w-4 h-4 inline" /></button>
        <button onClick={() => handleDelete(row.id)} className="bg-red-500 text-white rounded px-2 py-1 hover:bg-red-700 text-xs"><TrashIcon className="w-4 h-4 inline" /></button>
        <button onClick={() => handleReport(row.id)} className="bg-green-600 text-white rounded px-2 py-1 hover:bg-green-700 text-xs" title="Отчет">📊</button>
        <button onClick={() => handleAssignTariff(row)} className="bg-purple-600 text-white rounded px-2 py-1 hover:bg-purple-700 text-xs" title="Тариф">💰</button>
      </div>
    )}
  ];

  return (
    <div data-section="users">
      <Card title="Пользователи" icon={UserGroupIcon}>
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <SearchFilter value={searchQuery} onChange={v => { setSearchQuery(v); setCurrentPage(1); }} placeholder="Поиск по имени или email" className="flex-1" />
          <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2 border rounded-xl dark:bg-gray-800 dark:text-gray-100 text-sm">
            <option value="">Все роли</option>
            {uniqueRoles.map(r => <option key={r} value={r}>{roleLabels[r] || r}</option>)}
          </select>
        </div>
        <Table columns={userColumns} data={paginatedUsers} loading={loading} error={error} emptyMessage="Нет пользователей" />
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </Card>

      {/* Edit Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Редактировать пользователя" size="sm">
        <form onSubmit={handleSaveEdit} className="flex flex-col gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Имя</label>
            <input type="text" value={editForm.username} onChange={e => setEditForm(p => ({ ...p, username: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-800 dark:text-gray-100" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-800 dark:text-gray-100" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Роль</label>
            <select value={editForm.role} onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-800 dark:text-gray-100">
              <option value="user">Пользователь</option>
              <option value="moderator">Модератор</option>
              <option value="top_boss">Топ босс</option>
            </select>
          </div>
          {editForm.role === 'moderator' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Организация</label>
              <select value={editForm.organization_id || ''} onChange={e => setEditForm(p => ({ ...p, organization_id: e.target.value || null }))} className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-800 dark:text-gray-100">
                <option value="">Выберите организацию</option>
                {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Баланс токенов</label>
            <input type="number" value={editForm.tokens_balance} onChange={e => setEditForm(p => ({ ...p, tokens_balance: e.target.value === '' ? '' : parseInt(e.target.value) || 0 }))} className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-800 dark:text-gray-100" min="0" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm(p => ({ ...p, is_active: e.target.checked }))} /> Активен
          </label>
          <div className="flex gap-2 mt-2">
            <button type="submit" className="bg-blue-600 text-white rounded py-2 flex-1 hover:bg-blue-700">Сохранить</button>
            <button type="button" onClick={() => setShowEditModal(false)} className="bg-gray-300 text-gray-800 rounded py-2 flex-1 hover:bg-gray-400">Отмена</button>
          </div>
        </form>
      </Modal>

      {/* Report Modal */}
      <Modal isOpen={showReportModal} onClose={() => setShowReportModal(false)} title={`Отчет: ${userReport?.user?.username || ''}`} size="4xl">
        {userReport && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Статистика</h4>
                <div className="space-y-1 text-sm">
                  <div>Всего диалогов: <span className="font-medium">{userReport.statistics?.total_dialogs}</span></div>
                  <div>Завершено: <span className="font-medium">{userReport.statistics?.completed_dialogs}</span></div>
                  <div>Средний балл: <span className="font-medium">{userReport.statistics?.avg_score || 'Н/Д'}</span></div>
                  <div>Токенов использовано: <span className="font-medium">{userReport.statistics?.total_tokens_used}</span></div>
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">Тариф</h4>
                {userReport.tariff ? (
                  <div className="space-y-1 text-sm">
                    <div>Название: <span className="font-medium">{userReport.tariff.name}</span>{userReport.tariff.is_org_tariff && <span className="ml-1 text-xs text-blue-600">(орг)</span>}</div>
                    <div>Дневной лимит: <span className="font-medium">{userReport.tariff.daily_limit || 'Безлимит'}</span></div>
                  </div>
                ) : <div className="text-sm text-gray-600">Тариф не назначен</div>}
              </div>
            </div>
            {userReport.dialogs && userReport.dialogs.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Диалоги</h4>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {userReport.dialogs.map(d => (
                    <div key={d.id} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-medium text-sm">{d.scenario_name || 'Без сценария'}</div>
                        <div className="flex items-center gap-2">
                          {d.score && <span className="text-sm font-bold text-blue-600">{d.score}/10</span>}
                          <span className={`text-xs px-2 py-0.5 rounded ${d.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{d.status}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-400">{d.started_at ? new Date(d.started_at).toLocaleString() : ''}</div>
                      {d.analysis && (
                        <>
                          <button onClick={() => setExpandedReport(expandedReport === d.id ? null : d.id)} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 text-xs font-medium mt-2">
                            {expandedReport === d.id ? 'Скрыть отчёт ▲' : 'Показать отчёт ▼'}
                          </button>
                          {expandedReport === d.id && (
                            <div className="mt-2 text-xs text-gray-600 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none">
                              <MarkdownRenderer variant="report" darkMode={theme === 'dark'}>{d.analysis}</MarkdownRenderer>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Tariff Modal */}
      <Modal isOpen={showTariffModal} onClose={() => setShowTariffModal(false)} title="Назначить тариф" size="sm">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Тариф</label>
          <select value={tariffForm.tariff_id} onChange={e => setTariffForm({ tariff_id: e.target.value })} className="w-full px-4 py-2 rounded-xl border dark:bg-gray-800 dark:text-gray-100">
            <option value="">Без тарифа</option>
            {tariffs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSaveTariff} className="flex-1 bg-blue-600 text-white rounded py-2 hover:bg-blue-700">Назначить</button>
          <button onClick={() => setShowTariffModal(false)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded">Отмена</button>
        </div>
      </Modal>
    </div>
  );
};

export default UsersSection;
